
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, ArrowLeft, Save, AlertTriangle } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { toast } from "@/components/ui/sonner";

const SystemConfigurationSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    maintenanceMessage: 'System is under maintenance. Please check back later.',
    featureFlags: {
      enableAIAssistant: true,
      enableCertificates: true,
      enableAudioLearning: true,
      enableAdvancedAnalytics: false
    },
    systemLimits: {
      maxFileUploadSize: 10,
      maxCoursesPerUser: 50,
      maxModulesPerCourse: 100
    },
    cacheSettings: {
      enableCaching: true,
      cacheTimeout: 3600,
      clearCacheOnUpdate: true
    }
  });

  const handleSave = () => {
    toast.success("System configuration saved successfully");
  };

  const toggleMaintenanceMode = () => {
    setSettings({...settings, maintenanceMode: !settings.maintenanceMode});
    if (!settings.maintenanceMode) {
      toast.warning("Maintenance mode enabled - users will see maintenance message");
    } else {
      toast.success("Maintenance mode disabled - system is live");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')}>
          <ArrowLeft className="h-4 w-4" />
          Back to System Settings
        </Button>
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">System Configuration</h1>
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Maintenance Mode
            </CardTitle>
            <CardDescription>Control system availability and maintenance status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Enable Maintenance Mode</Label>
                <p className="text-sm text-muted-foreground">Put the system in maintenance mode</p>
              </div>
              <Switch
                checked={settings.maintenanceMode}
                onCheckedChange={toggleMaintenanceMode}
              />
            </div>
            
            {settings.maintenanceMode && (
              <div>
                <Label htmlFor="maintenanceMessage">Maintenance Message</Label>
                <Textarea
                  id="maintenanceMessage"
                  value={settings.maintenanceMessage}
                  onChange={(e) => setSettings({...settings, maintenanceMessage: e.target.value})}
                  placeholder="Enter message to display to users during maintenance"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Feature Flags</CardTitle>
            <CardDescription>Enable or disable platform features</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>AI Assistant</Label>
              <Switch
                checked={settings.featureFlags.enableAIAssistant}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  featureFlags: {...settings.featureFlags, enableAIAssistant: checked}
                })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Certificates</Label>
              <Switch
                checked={settings.featureFlags.enableCertificates}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  featureFlags: {...settings.featureFlags, enableCertificates: checked}
                })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Audio Learning</Label>
              <Switch
                checked={settings.featureFlags.enableAudioLearning}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  featureFlags: {...settings.featureFlags, enableAudioLearning: checked}
                })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Advanced Analytics</Label>
              <Switch
                checked={settings.featureFlags.enableAdvancedAnalytics}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  featureFlags: {...settings.featureFlags, enableAdvancedAnalytics: checked}
                })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Limits</CardTitle>
            <CardDescription>Configure platform usage limits and restrictions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="maxFileSize">Max File Upload (MB)</Label>
                <Input
                  id="maxFileSize"
                  type="number"
                  value={settings.systemLimits.maxFileUploadSize}
                  onChange={(e) => setSettings({
                    ...settings,
                    systemLimits: {...settings.systemLimits, maxFileUploadSize: parseInt(e.target.value)}
                  })}
                />
              </div>
              <div>
                <Label htmlFor="maxCourses">Max Courses Per User</Label>
                <Input
                  id="maxCourses"
                  type="number"
                  value={settings.systemLimits.maxCoursesPerUser}
                  onChange={(e) => setSettings({
                    ...settings,
                    systemLimits: {...settings.systemLimits, maxCoursesPerUser: parseInt(e.target.value)}
                  })}
                />
              </div>
              <div>
                <Label htmlFor="maxModules">Max Modules Per Course</Label>
                <Input
                  id="maxModules"
                  type="number"
                  value={settings.systemLimits.maxModulesPerCourse}
                  onChange={(e) => setSettings({
                    ...settings,
                    systemLimits: {...settings.systemLimits, maxModulesPerCourse: parseInt(e.target.value)}
                  })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cache Settings</CardTitle>
            <CardDescription>Configure system caching and performance settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable Caching</Label>
              <Switch
                checked={settings.cacheSettings.enableCaching}
                onCheckedChange={(checked) => setSettings({
                  ...settings,
                  cacheSettings: {...settings.cacheSettings, enableCaching: checked}
                })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cacheTimeout">Cache Timeout (seconds)</Label>
                <Input
                  id="cacheTimeout"
                  type="number"
                  value={settings.cacheSettings.cacheTimeout}
                  onChange={(e) => setSettings({
                    ...settings,
                    cacheSettings: {...settings.cacheSettings, cacheTimeout: parseInt(e.target.value)}
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Clear Cache on Update</Label>
                <Switch
                  checked={settings.cacheSettings.clearCacheOnUpdate}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    cacheSettings: {...settings.cacheSettings, clearCacheOnUpdate: checked}
                  })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SystemConfigurationSettings;
